// =====================================================================
// Faz AM (2026-05-07) — Owner Notes / Sticky Memos
// =====================================================================
// Owner panele post-it kartları — günlük operasyon hatırlatıcı.
// Customer 360 / Order detail / Product 360 sayfalarına da scope'lu
// olarak eklenebilir (bu sayfa "general" + tüm scope'ları gösterir).
//
// Endpoint:
//   GET /api/admin/notes?status=active&scope=general
//   POST /api/admin/notes
//   PUT /api/admin/notes/:id (body/color/status)
//   PUT /api/admin/notes/:id/toggle
//   DELETE /api/admin/notes/:id
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Button, TextField, ToggleButton, ToggleButtonGroup, Checkbox
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
}) : "—";

// Renk → bg + border (post-it görünümü)
const COLOR_PALETTE = {
  yellow: { bg: "#fef9c3", border: "#facc15" },
  red:    { bg: "#fee2e2", border: "#ef4444" },
  green:  { bg: "#d1fae5", border: "#10b981" },
  blue:   { bg: "#dbeafe", border: "#3b82f6" },
  purple: { bg: "#ede9fe", border: "#8b5cf6" }
};

// Scope → Türkçe etiket + ikon
const SCOPE_META = {
  general:  { label: "Genel",     icon: "📌" },
  customer: { label: "Müşteri",   icon: "👤" },
  order:    { label: "Sipariş",   icon: "📦" },
  product:  { label: "Ürün",      icon: "🏷️" }
};

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState("active");  // active | done | all

  // Yeni not formu state
  const [newBody, setNewBody] = useState("");
  const [newColor, setNewColor] = useState("yellow");

  const load = () => {
    setLoading(true);
    const params = filterStatus === "all" ? "" : `?status=${filterStatus}`;
    API.get(`/api/admin/notes${params}`)
      .then(r => { setNotes(r.data.items || []); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filterStatus]);

  const submitNew = async () => {
    if (!newBody.trim()) { toast.error("Not boş olamaz"); return; }
    try {
      await API.post("/api/admin/notes", {
        scope: "general",  // V1: sadece genel notlar (Customer 360'tan scoped sonra eklenir)
        body: newBody.trim(),
        color: newColor
      });
      toast.success("Not eklendi");
      setNewBody("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const toggleStatus = async (id) => {
    try {
      await API.put(`/api/admin/notes/${id}/toggle`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu not silinsin mi?")) return;
    try {
      await API.delete(`/api/admin/notes/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const archive = async (id) => {
    try {
      await API.put(`/api/admin/notes/${id}`, { status: "archived" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  if (loading && notes.length === 0) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Notlarım</Typography>
          <Typography variant="body2" color="text.secondary">
            Post-it tarzı sticky note'lar — günlük operasyon hatırlatıcısı
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup size="small" value={filterStatus} exclusive onChange={(_, v) => v && setFilterStatus(v)}>
            <ToggleButton value="active">Aktif</ToggleButton>
            <ToggleButton value="done">Bitenler</ToggleButton>
            <ToggleButton value="all">Tümü</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Yeni not ekleme formu */}
      <Card sx={{ p: 2, borderRadius: 3, mb: 3, bgcolor: COLOR_PALETTE[newColor].bg, border: `2px solid ${COLOR_PALETTE[newColor].border}` }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            fullWidth multiline rows={2}
            placeholder="Yeni not yaz... (örn: 'Yarın Ahmet Bey'i ara, fatura kontrolü yap, X ürünü stok bitiyor')"
            value={newBody}
            onChange={e => setNewBody(e.target.value)}
            onKeyDown={e => {
              // Ctrl+Enter ile hızlı kaydet
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submitNew();
            }}
            sx={{ flex: 1, minWidth: 250, "& .MuiInputBase-root": { bgcolor: "white" } }}
          />
          {/* Renk seçici — 5 renk küçük circle */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {Object.keys(COLOR_PALETTE).map(c => (
                <Box
                  key={c}
                  onClick={() => setNewColor(c)}
                  sx={{
                    width: 28, height: 28, borderRadius: "50%", cursor: "pointer",
                    bgcolor: COLOR_PALETTE[c].border,
                    border: newColor === c ? "3px solid #1f2937" : "3px solid transparent",
                    transition: "transform 0.1s",
                    "&:hover": { transform: "scale(1.1)" }
                  }}
                  title={c}
                />
              ))}
            </Box>
            <Button
              variant="contained" startIcon={<AddIcon />}
              onClick={submitNew}
              disabled={!newBody.trim()}
              sx={{ bgcolor: "#1565C0" }}
            >
              Ekle
            </Button>
          </Box>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
          💡 İpucu: <strong>Ctrl+Enter</strong> ile hızlı kaydet. Müşteri/sipariş/ürün'e özel notlar Customer 360 sayfasından eklenir.
        </Typography>
      </Card>

      {/* KPI'lar — her renk grupta kaç tane */}
      <Grid container spacing={1.5} mb={3}>
        <Grid item xs={6} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">Toplam</Typography>
            <Typography variant="h5" fontWeight={800}>{notes.length}</Typography>
          </Card>
        </Grid>
        {Object.entries(COLOR_PALETTE).map(([color, palette]) => (
          <Grid item xs={6} md={2.4} key={color}>
            <Card sx={{ p: 1.5, borderRadius: 3, textAlign: "center", bgcolor: palette.bg }}>
              <Typography variant="caption" color="text.secondary">{color}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: palette.border }}>
                {notes.filter(n => n.color === color).length}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Not kartları grid */}
      {notes.length === 0 ? (
        <Alert severity="info">
          {filterStatus === "active" ? "Aktif not yok — yukarıdan yeni ekleyebilirsin"
            : filterStatus === "done" ? "Bitmiş not yok"
            : "Hiç not yok"}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {notes.map(note => {
            const palette = COLOR_PALETTE[note.color] || COLOR_PALETTE.yellow;
            const scopeMeta = SCOPE_META[note.scope] || SCOPE_META.general;
            const isDone = note.status === "done";
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={note.id}>
                <Card sx={{
                  p: 2, borderRadius: 3, height: "100%",
                  bgcolor: palette.bg,
                  border: `2px solid ${palette.border}`,
                  opacity: isDone ? 0.6 : 1,
                  position: "relative",
                  display: "flex", flexDirection: "column",
                  // Post-it görünümü: hafif gölge + dönüklük (transform random'a göre yok, basit)
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Checkbox
                      checked={isDone}
                      onChange={() => toggleStatus(note.id)}
                      size="small"
                      sx={{ p: 0 }}
                    />
                    <Chip
                      label={`${scopeMeta.icon} ${scopeMeta.label}${note.scope_id ? ` #${note.scope_id}` : ""}`}
                      size="small"
                      sx={{ height: 20, fontSize: 10, bgcolor: "rgba(255,255,255,0.6)" }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => remove(note.id)} sx={{ color: "#94a3b8" }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      whiteSpace: "pre-wrap",
                      textDecoration: isDone ? "line-through" : "none",
                      color: isDone ? "#64748b" : "#1f2937",
                      fontSize: 13,
                      lineHeight: 1.5
                    }}
                  >
                    {note.body}
                  </Typography>

                  <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.disabled" fontSize={10}>
                      {fmtDate(note.created_at)} · {note.author_name || "admin"}
                    </Typography>
                    {isDone && (
                      <Button size="small" onClick={() => archive(note.id)} sx={{ fontSize: 10, minWidth: 0, p: 0.5 }}>
                        Arşivle
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
